import { FC, PropsWithChildren, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useGetConsents } from 'src/hooks/useGetConsents';
import useLogin from 'src/hooks/useLogin';
import useSelectedTab from 'src/hooks/useSelectedTab';
import useStompSubscriber from 'src/hooks/useStomp';
import { restorePushNotification } from 'src/push';
import { useInitModelRegistry } from 'src/stores/downloadableContents/hooks';
import { signUpSettingsLoadableAtom } from 'src/stores/register/atoms';
import { visitorBirthAtom } from 'src/stores/termsAge/atoms';
import useCheckLegalUpdates from './useCheckLegalUpdates';
import useGetVisitorConfigs from './useGetVisitorConfigs';
import useInduceRegisterModal from './useInduceRegisterModal';
import useLoginUserOrderedModal from './useLoginUserOrderedModal';
import { useOnline } from './useOnline';
import useParticipateCampaigns from './useParticipateCampaigns';
import useScreenOpen from './useScreenOpen';
import useSessionEnd from './useSessionEnd';
import useUpdateInventory from './useUpdateInventory';
import useUpdateUserInfo from './useUpdateUserInfo';

/**
 * 아자르웹에서 제공되는 일반적인 페이지들에 공통적으로 적용되는 로직을 반영하기 위해 사용
 */
const MainAppWrapper: FC<PropsWithChildren> = ({
  children
}) => {
  const setVisitorBirth = useSetAtom(visitorBirthAtom);
  const {
    isLogin
  } = useLogin();
  const {
    serverSideTabName,
    reportSelectedTab
  } = useSelectedTab();
  useGetVisitorConfigs();
  useInitModelRegistry();
  useInduceRegisterModal();

  // prefetch
  // 회원가입 퍼널 UX에서 로딩을 피하기 위해, 생년월일 A/B 테스트 설정을 미리 로드
  useAtomValue(signUpSettingsLoadableAtom);
  // 회원가입 퍼널 UX에서 로딩을 피하기 위해, 약관 A/B 테스트 설정을 미리 로드
  useGetConsents();

  // 로그인 상태 유저 전용 (eg. 정보 갱신)
  useUpdateUserInfo();
  useLoginUserOrderedModal();
  useUpdateInventory();
  useCheckLegalUpdates();
  useParticipateCampaigns();

  // 정보 수집용
  useScreenOpen();
  useSessionEnd();
  useOnline();
  const {
    isStompReady
  } = useStompSubscriber();
  useEffect(() => {
    /**
     * 앱과 웹의 탭 종류 불일치로 현재 select-tab API에서 처리할 수 있는 탭의 종류가 제한됨 (앱의 API를 그대로 활용)
     * 추가적으로 select-tab에 대한 수요가 현재 미러탭밖에 없기 때문에 미러에 한해서 API 호출
     * 추후 다른 탭에 대한 수요가 생기면 API 호출 범위를 확장
     */
    if (serverSideTabName === 'MIRROR' && isLogin && isStompReady) {
      reportSelectedTab();
    }
  }, [serverSideTabName, reportSelectedTab, isLogin, isStompReady]);
  useEffect(() => {
    if (isLogin) {
      setVisitorBirth(null); // 로그인하면 비지터 나이 초기화
      restorePushNotification();
    }
  }, [isLogin, setVisitorBirth]);
  return <>{children}</>;
};
export default MainAppWrapper;